import { Component, inject, Inject, OnDestroy, OnInit } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { Subject, takeUntil } from "rxjs";

// Global, unencapsulated stylesheet: injected via this side-effect import, not Angular's
// styleUrls/styleUrl, so it isn't scoped to this component.
import "./scenario-difference-message.component.style.scss";
import {
  ScenarioAccountInfo,
  ScenarioOptionsType,
} from "@bitwarden/web-vault/app/models/types/scenario-group.types";

import { ScenarioDisplayView } from "@bitwarden/web-vault/app/models/view/scenario/scenario-display.view";
import { DialogFactory } from "@bitwarden/web-vault/app/components/dialog/dialogFactory";
import { DeviceDetectorService } from "ngx-device-detector";

// Temporary prod debug for zube 14318 (same flag as current-interest-rate.ts): opt in per device with
// localStorage.setItem("scenarioDebug", "1") so user balances never hit the console by default.
const isScenarioDebug = () => globalThis.localStorage?.getItem("scenarioDebug") === "1";

export const closeScenDiffMessage = {
  continue: "continue",
};

type ScenarioDiffBlock = {
  textOne: string | any;
  actionButton?: string;
  numberToShow: string;
  textTwo: string;
};
type ScenarioDifferenceMessage = {
  headerText: string;
  headerIcon: "institutions" | "check" | "info";
  buttonText: string;
  firstBlock: ScenarioDiffBlock;
  secondBlock: ScenarioDiffBlock;
  thirdBlock: ScenarioDiffBlock;
  moreThanCurrent?: number;
  moreThanCurrentPercent?: number;
  bestAccount?: ScenarioAccountInfo;
  currentBalanceEarned?: number;
};

@Component({
  selector: "app-scenario-difference-message",
  templateUrl: "./scenario-difference-message.component.html",
})
export class ScenarioDifferenceMessageComponent implements OnInit, OnDestroy {
  scenarioDifferenceMessage: ScenarioDifferenceMessage = null;
  isVisible = false;
  private destroy$ = new Subject<void>();
  protected dialogFactory: DialogFactory;
  protected deviceService: DeviceDetectorService = inject(DeviceDetectorService);
  protected invalidScenarioDiffMsg: boolean = false;
  protected currentBalance: number;
  protected isZeroBalance: boolean = false;

  protected scenarioDifferenceConstant = {
    willPayYou: "willPayYou",
    more: "more",
    over12Months: "overTheNext12Months",
  };

  constructor(
    public dialogRef: MatDialogRef<ScenarioDifferenceMessageComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: { scenarioData: ScenarioDisplayView[]; coreCurrency: string }, // The data passed to the dialog
  ) {
    this.dialogFactory = new DialogFactory();
  }

  ngOnInit(): void {
    this.setScenarioDifferenceMessage();
  }

  protected firstRollNumSize = this.deviceService.isDesktop() ? 60 : 35;
  protected secondRollNumSize = this.deviceService.isDesktop() ? 35 : 20;

  getScenario(scenarioType: ScenarioOptionsType) {
    return this.data?.scenarioData?.find(
      (scenario: ScenarioDisplayView) => scenario.scenarioType === scenarioType,
    );
  }

  setScenarioDifferenceMessage() {
    const bestNewInterestRateResult = this.getScenario("bestNewInterestRate")?.finalBalanceAmount;
    const currentInterestRateResult = this.getScenario("currentInterestRate")?.finalBalanceAmount;
    this.currentBalance = Number(
      this.getScenario("currentInterestRate")?.helpInfo?.totalBalance?.toFixed(2),
    );
    this.isZeroBalance = this.currentBalance === 0;
    this.invalidScenarioDiffMsg = !this.currentBalance || this.isZeroBalance;
    const moreThanCurrentAccount = Number(
      (bestNewInterestRateResult - currentInterestRateResult).toFixed(2),
    );
    const currentBalanceEarned = currentInterestRateResult - this.currentBalance;
    const moreThanCurrentAccountPercent = Number(
      ((moreThanCurrentAccount / currentBalanceEarned) * 100).toFixed(2),
    );

    //the calculation is always 12 months difference
    const monthDif = "12";

    const isMaximized =
      bestNewInterestRateResult <= currentInterestRateResult && currentInterestRateResult != 0;

    if (isScenarioDebug()) {
      const bestHelpInfo = this.getScenario("bestNewInterestRate")?.helpInfo;
      // eslint-disable-next-line no-console -- temporary prod debug for zube 14318, opt-in via localStorage
      console.log("[scenario-diff] dialog", {
        bestNewInterestRateResult,
        currentInterestRateResult,
        totalBalance: this.getScenario("currentInterestRate")?.helpInfo?.totalBalance,
        currentBalance: this.currentBalance,
        moreThanCurrentAccount,
        currentBalanceEarned,
        moreThanCurrentAccountPercent,
        isMaximized,
        invalidScenarioDiffMsg: this.invalidScenarioDiffMsg,
        transferToAccount: bestHelpInfo?.transferToAccount,
        transferAmount: bestHelpInfo?.transferAmount,
      });
    }

    if (this.invalidScenarioDiffMsg) {
      const overdraftMessage =
        "The Gloss Vault does not yet have the necessary information to perform calculations on overdrawn accounts";
      const overDraftMessage2 = "Notify our team and we will add the data for you.";
      const contactUs = "Contact Us";
      const currentBalanceMessage = "Your account balance is currently";
      const addPositiveBalanceMessage = `Add a positive balance to your account for more insight into the interest you will earn in the next ${monthDif} months`;

      this.scenarioDifferenceMessage = {
        headerText: "Oh no!",
        headerIcon: "info",
        buttonText: "Continue",
        firstBlock: {
          textOne: this.isZeroBalance ? currentBalanceMessage : overdraftMessage,
          numberToShow: this.isZeroBalance ? "0.00" : null,
          textTwo: null,
          actionButton: !this.isZeroBalance ? "Contact us" : null,
        },
        secondBlock: {
          textOne: this.isZeroBalance ? null : overDraftMessage2,
          numberToShow: null,
          textTwo: null,
        },
        thirdBlock: {
          textOne: this.isZeroBalance ? addPositiveBalanceMessage : contactUs,
          numberToShow: null,
          textTwo: null,
        },
      };
      return;
    }

    if (isMaximized) {
      this.scenarioDifferenceMessage = {
        headerText: "Well Done!",
        headerIcon: "check",
        buttonText: "Find out how",
        firstBlock: {
          textOne: "You are already maximizing your savings interest",
          numberToShow: null,
          textTwo: null,
        },
        secondBlock: {
          textOne: "On your",
          numberToShow: this.currentBalance.toString(),
          textTwo: "balance",
        },
        thirdBlock: {
          textOne: "You will earn",
          numberToShow: currentBalanceEarned.toFixed(2),
          textTwo: `in the next ${monthDif} months`,
        },
      };
    } else {
      const bestAccount: ScenarioAccountInfo =
        this.getScenario("bestNewInterestRate")?.helpInfo.transferToAccount;
      this.scenarioDifferenceMessage = {
        ...this.scenarioDifferenceMessage,
        headerText: bestAccount.accountBank,
        headerIcon: "institutions",
        buttonText: "Find out how",
        bestAccount: bestAccount,
        moreThanCurrent: moreThanCurrentAccount,
        moreThanCurrentPercent: moreThanCurrentAccountPercent,
        currentBalanceEarned: currentBalanceEarned,
      };
    }
  }

  openHelpBox() {
    this.dialogFactory.callHelpBox();
  }

  async closeDialogBtn() {
    return this.invalidScenarioDiffMsg
      ? this.dialogRef.close()
      : this.dialogRef.close(closeScenDiffMessage.continue);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
